import type { SWRConfiguration } from 'swr';

import { useMemo } from 'react';
import useSWR, { mutate } from 'swr';

import axiosInstance, { fetcher, endpoints } from 'src/lib/axios-artham';

// ----------------------------------------------------------------------

export type NotificationAction = {
  type: 'approve' | 'reject' | 'request_info' | 'view_details' | 'retry';
  label: string;
  variant: 'primary' | 'danger' | 'secondary' | 'default';
  endpoint?: string;
  requiresNote?: boolean;
};

export type NotificationItem = {
  id: string;
  type: string;
  title: string;
  category: string;
  priority?: 'high' | 'medium' | 'low';
  targetRole?: 'admin' | 'dealer' | 'distributor';
  metadata?: Record<string, any>;
  actions?: NotificationAction[];
  isUnRead: boolean;
  avatarUrl: string | null;
  createdAt: string | number | null;
  readAt?: string | null;
};

export type NotificationListData = {
  success: boolean;
  data: NotificationItem[];
  total: number;
  message?: string;
  error?: string;
};

export type NotificationCountData = {
  success: boolean;
  data: { count: number };
};

// ----------------------------------------------------------------------

const notificationQueryKeys = {
  list: (params?: any) => [endpoints.notifications.list, { params }],
  unreadCount: () => [endpoints.notifications.unreadCount],
};

// ----------------------------------------------------------------------

/**
 * Refresh notification queries
 */
export function refreshNotificationQueries() {
  mutate((key) => {
    if (Array.isArray(key)) {
      const [url] = key;
      return typeof url === 'string' && url.includes('/notifications');
    }
    return false;
  });
}

// ----------------------------------------------------------------------

/**
 * Hook to get notifications list
 */
export function useGetNotifications(
  params?: {
    limit?: number;
    offset?: number;
    unreadOnly?: boolean;
    category?: string;
    priority?: string;
  },
  options?: SWRConfiguration
) {
  const { data, error, isLoading } = useSWR<NotificationListData>(
    notificationQueryKeys.list(params),
    fetcher,
    {
      ...options,
      revalidateOnFocus: true,
      refreshInterval: 30000, // Refresh every 30 seconds
    }
  );

  const memoizedValue = useMemo(
    () => ({
      notifications: data?.data || [],
      total: data?.total || 0,
      notificationsLoading: isLoading,
      notificationsError: error,
      notificationsEmpty: !isLoading && !data?.data?.length,
    }),
    [data, error, isLoading]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

/**
 * Hook to get unread notification count
 */
export function useGetNotificationCount(options?: SWRConfiguration) {
  const { data, error, isLoading } = useSWR<NotificationCountData>(
    notificationQueryKeys.unreadCount(),
    fetcher,
    {
      ...options,
      revalidateOnFocus: true,
      refreshInterval: 30000, // Refresh every 30 seconds
    }
  );

  const memoizedValue = useMemo(
    () => ({
      count: data?.data?.count || 0,
      countLoading: isLoading,
      countError: error,
    }),
    [data, error, isLoading]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

/**
 * Mark notification as read
 */
export async function markNotificationAsRead(notificationId: string): Promise<NotificationItem> {
  try {
    const response = await axiosInstance.patch<{
      success: boolean;
      data: NotificationItem;
      message?: string;
    }>(endpoints.notifications.markRead(notificationId));

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to mark notification as read');
    }

    // Refresh notification queries
    refreshNotificationQueries();

    return response.data.data;
  } catch (error: any) {
    console.error('Error marking notification as read:', error);
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to mark notification as read'
    );
  }
}

// ----------------------------------------------------------------------

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsAsRead(): Promise<number> {
  try {
    const response = await axiosInstance.patch<{
      success: boolean;
      data: { count: number };
      message?: string;
    }>(endpoints.notifications.markAllRead);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to mark all notifications as read');
    }

    // Refresh notification queries
    refreshNotificationQueries();

    return response.data.data.count;
  } catch (error: any) {
    console.error('Error marking all notifications as read:', error);
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to mark all notifications as read'
    );
  }
}

// ----------------------------------------------------------------------

/**
 * Delete a notification
 */
export async function deleteNotification(notificationId: string): Promise<void> {
  try {
    const response = await axiosInstance.delete<{
      success: boolean;
      message?: string;
    }>(endpoints.notifications.delete(notificationId));

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete notification');
    }

    // Refresh notification queries
    refreshNotificationQueries();
  } catch (error: any) {
    console.error('Error deleting notification:', error);
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to delete notification'
    );
  }
}

// ----------------------------------------------------------------------

/**
 * Create test notification (development only)
 */
export async function createTestNotification(
  type?: string,
  priority?: string
): Promise<NotificationItem> {
  try {
    const response = await axiosInstance.post<{
      success: boolean;
      data: NotificationItem;
      message?: string;
    }>(endpoints.notifications.test, { type, priority });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create test notification');
    }

    // Refresh notification queries
    refreshNotificationQueries();

    return response.data.data;
  } catch (error: any) {
    console.error('Error creating test notification:', error);
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to create test notification'
    );
  }
}

// ----------------------------------------------------------------------

/**
 * Handle notification action (approve, reject, etc.)
 */
export async function handleNotificationAction(
  action: NotificationAction,
  metadata?: Record<string, any>,
  additionalData?: any
): Promise<any> {
  try {
    if (!action.endpoint) {
      throw new Error('Action endpoint not defined');
    }

    let response;
    const endpoint = action.endpoint
      .replace(':transactionId', metadata?.transactionId || '')
      .replace(':requestId', metadata?.requestId || '')
      .replace(':id', metadata?.transactionId || metadata?.requestId || '');

    switch (action.type) {
      case 'approve':
        response = await axiosInstance.patch(endpoint, additionalData);
        break;

      case 'reject':
        response = await axiosInstance.patch(endpoint, {
          reason: additionalData?.reason || 'Rejected by admin',
          ...additionalData,
        });
        break;

      case 'request_info':
        response = await axiosInstance.post(endpoint, {
          message: additionalData?.message || 'Additional information required',
          ...additionalData,
        });
        break;

      case 'retry':
        response = await axiosInstance.post(endpoint, additionalData);
        break;

      case 'view_details':
        // This doesn't need an API call, just navigation
        return { navigate: endpoint };

      default:
        throw new Error(`Unknown action type: ${action.type}`);
    }

    if (!response.data.success) {
      throw new Error(response.data.message || 'Action failed');
    }

    // Refresh relevant queries based on action
    if (action.endpoint.includes('transaction')) {
      // Refresh transaction queries
      mutate((key) => {
        if (Array.isArray(key)) {
          const [url] = key;
          return typeof url === 'string' && url.includes('/transaction');
        }
        return false;
      });
    } else if (action.endpoint.includes('credit')) {
      // Refresh credit queries
      mutate((key) => {
        if (Array.isArray(key)) {
          const [url] = key;
          return typeof url === 'string' && url.includes('/credit');
        }
        return false;
      });
    }

    // Always refresh notifications
    refreshNotificationQueries();

    return response.data;
  } catch (error: any) {
    console.error('Error handling notification action:', error);
    throw new Error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to perform action'
    );
  }
}
